import React, { useState, useEffect } from 'react';
import ChatConsole from './ChatConsole';
import ResizableSplitter from './ResizableSplitter';
import './DualConsoleContainer.css';

// Map font size names to CSS values
const FONT_SIZE_MAP = {
    small: '0.85rem',
    medium: '0.95rem',
    large: '1.05rem',
    xlarge: '1.15rem'
};

function DualConsoleContainer({ 
    sendMessage, 
    showSecondConsole, 
    layout = 'horizontal',
    consoleSwapped = false,
    onContextMenu,
    consoleFontFamily = 'M PLUS 1 Code',
    consoleFontSize = 'medium'
}) {
    const [splitPosition, setSplitPosition] = useState(0.5);

    // Load split position from localStorage on mount
    useEffect(() => {
        const savedPosition = localStorage.getItem('console-split-position');
        if (savedPosition) {
            const parsed = parseFloat(savedPosition);
            if (!isNaN(parsed) && parsed >= 0.2 && parsed <= 0.8) {
                setSplitPosition(parsed);
            }
        }
    }, []);

    // Save split position to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('console-split-position', splitPosition.toString());
    }, [splitPosition]);

    const handleResize = (newPosition) => {
        setSplitPosition(newPosition);
    };

    const handleContextMenu = (e) => {
        e.preventDefault();
        if (onContextMenu) {
            onContextMenu(e.clientX, e.clientY);
        }
    };

    const isHorizontal = layout === 'horizontal';
    const firstSize = showSecondConsole ? splitPosition : 1;
    const secondSize = 1 - splitPosition;

    // Create console elements
    const primaryConsole = (
        <ChatConsole sendMessage={sendMessage} consoleId="primary" />
    );
    const secondaryConsole = (
        <ChatConsole sendMessage={sendMessage} consoleId="secondary" />
    );

    // Determine which console goes first based on swap state
    const firstConsole = consoleSwapped ? secondaryConsole : primaryConsole;
    const secondConsole = consoleSwapped ? primaryConsole : secondaryConsole;

    // Build CSS custom properties for font settings
    const fontStyles = {
        '--console-font-family': `"${consoleFontFamily}", monospace`,
        '--console-font-size': FONT_SIZE_MAP[consoleFontSize] || FONT_SIZE_MAP.medium
    };

    return (
        <div 
            className={`dual-console-container ${isHorizontal ? 'horizontal' : 'vertical'}`}
            onContextMenu={handleContextMenu}
            style={fontStyles}
        >
            <div 
                className={`console-panel ${consoleSwapped ? 'console-secondary' : 'console-primary'}`}
                style={{
                    [isHorizontal ? 'width' : 'height']: `${firstSize * 100}%`
                }}
            >
                {firstConsole}
            </div>
            
            {showSecondConsole && (
                <>
                    <ResizableSplitter 
                        orientation={layout} 
                        onResize={handleResize}
                        minSize={0.2}
                    />
                    <div 
                        className={`console-panel ${consoleSwapped ? 'console-primary' : 'console-secondary'}`}
                        style={{
                            [isHorizontal ? 'width' : 'height']: `${secondSize * 100}%`
                        }}
                    >
                        {secondConsole}
                    </div>
                </>
            )}
        </div>
    );
}

export default DualConsoleContainer;
